import React, { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface LocationData {
  lat: number;
  lng: number;
  accuracy?: number;
  timestamp?: number;
}

export const useLocationTracking = () => {
  const { user } = useAuth();
  const [isSharing, setIsSharing] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<LocationData | null>(null);
  const [loading, setLoading] = useState(false);
  const [isNative, setIsNative] = useState(false);
  const watchId = React.useRef<string | number | null>(null);

  useEffect(() => {
    setIsNative(Capacitor.isNativePlatform());
  }, []);

  // Check current sharing status
  useEffect(() => {
    if (user) {
      checkSharingStatus();
    }
  }, [user]);

  const checkSharingStatus = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('user_locations')
        .select('is_sharing_enabled')
        .eq('user_id', user.id)
        .single();

      if (data) {
        setIsSharing(data.is_sharing_enabled);
      }
    } catch (error) {
      console.log('No existing location record:', error);
    }
  };

  const requestLocationPermission = async (): Promise<boolean> => {
    try {
      if (isNative) {
        // Use Capacitor for native platforms
        console.log('Requesting location permissions via Capacitor...');
        const permissions = await Geolocation.requestPermissions();
        
        if (permissions.location !== 'granted') {
          toast.error('Permesso di geolocalizzazione negato. Abilitalo nelle impostazioni dell\'app.');
          return false;
        }

        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 15000
        });

        const locationData: LocationData = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp
        };

        setCurrentLocation(locationData);
        console.log('Location obtained via Capacitor:', locationData);
        return true;
      } else {
        // Fallback to web geolocation API
        console.log('Using web geolocation API...');
        if (!navigator.geolocation) {
          toast.error('La geolocalizzazione non è supportata dal tuo browser');
          return false;
        }

        return new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const locationData: LocationData = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
                accuracy: position.coords.accuracy,
                timestamp: position.timestamp
              };
              setCurrentLocation(locationData);
              console.log('Location obtained via web API:', locationData);
              resolve(true);
            },
            (error) => {
              console.error('Geolocation error:', error);
              let errorMessage = '';
              
              switch (error.code) {
                case error.PERMISSION_DENIED:
                  errorMessage = 'Permesso di geolocalizzazione negato. Abilitalo nelle impostazioni del browser.';
                  break;
                case error.POSITION_UNAVAILABLE:
                  errorMessage = 'Posizione non disponibile. Controlla la connessione GPS.';
                  break;
                case error.TIMEOUT:
                  errorMessage = 'Timeout nella richiesta di posizione. Riprova.';
                  break;
                default:
                  errorMessage = 'Errore sconosciuto nella geolocalizzazione.';
                  break;
              }
              
              toast.error(errorMessage);
              resolve(false);
            },
            {
              enableHighAccuracy: true,
              timeout: 15000,
              maximumAge: 30000
            }
          );
        });
      }
    } catch (error) {
      console.error('Location permission error:', error);
      toast.error('Errore nel richiedere i permessi di geolocalizzazione');
      return false;
    }
  };

  const getCurrentPosition = async (): Promise<LocationData | null> => {
    try {
      if (isNative) {
        console.log('Getting current position via Capacitor...');
        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 15000
        });

        const locationData = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp
        };

        console.log('Current position via Capacitor:', locationData);
        return locationData;
      } else {
        console.log('Getting current position via web API...');
        if (!navigator.geolocation) {
          throw new Error('Geolocation not supported');
        }

        return new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const locationData = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
                accuracy: position.coords.accuracy,
                timestamp: position.timestamp
              };
              console.log('Current position via web API:', locationData);
              resolve(locationData);
            },
            (error) => {
              console.error('Get position error:', error);
              reject(error);
            },
            {
              enableHighAccuracy: true,
              timeout: 15000,
              maximumAge: 30000
            }
          );
        });
      }
    } catch (error) {
      console.error('Get current position error:', error);
      return null;
    }
  };

  const updateLocationInDB = async (locationData: LocationData) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('user_locations')
        .upsert({
          user_id: user.id,
          latitude: locationData.lat,
          longitude: locationData.lng,
          accuracy: locationData.accuracy,
          is_sharing_enabled: true,
          last_updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;
      console.log('Location updated in database:', locationData);
    } catch (error) {
      console.error('Error updating location:', error);
      toast.error('Errore nell\'aggiornamento della posizione');
    }
  };

  const startLocationTracking = async () => {
    try {
      if (isNative) {
        console.log('Starting location tracking via Capacitor...');
        // Use Capacitor watchPosition for native platforms
        watchId.current = await Geolocation.watchPosition(
          {
            enableHighAccuracy: true,
            timeout: 10000
          },
          (position) => {
            if (position) {
              const locationData: LocationData = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
                accuracy: position.coords.accuracy,
                timestamp: position.timestamp
              };
              console.log('Location update via Capacitor:', locationData);
              setCurrentLocation(locationData);
              updateLocationInDB(locationData);
            }
          }
        );
      } else {
        console.log('Starting location tracking via web API...');
        // Fallback to web geolocation API
        if (!navigator.geolocation) return;

        watchId.current = navigator.geolocation.watchPosition(
          (position) => {
            const locationData: LocationData = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
              accuracy: position.coords.accuracy,
              timestamp: position.timestamp
            };
            console.log('Location update via web API:', locationData);
            setCurrentLocation(locationData);
            updateLocationInDB(locationData);
          },
          (error) => {
            console.error('Location tracking error:', error);
          },
          {
            enableHighAccuracy: true,
            timeout: 30000,
            maximumAge: 10000
          }
        );
      }
    } catch (error) {
      console.error('Start location tracking error:', error);
    }
  };

  const stopLocationTracking = async () => {
    try {
      if (watchId.current !== null) {
        if (isNative) {
          await Geolocation.clearWatch({ id: watchId.current as string });
        } else {
          navigator.geolocation.clearWatch(watchId.current as number);
        }
        watchId.current = null;
        console.log('Location tracking stopped');
      }
    } catch (error) {
      console.error('Stop location tracking error:', error);
    }
  };

  const enableLocationSharing = async () => {
    if (!user) return false;

    setLoading(true);
    try {
      console.log('Enabling location sharing...');
      // Request permission first
      const hasPermission = await requestLocationPermission();
      if (!hasPermission) {
        setLoading(false);
        return false;
      }

      // Start tracking
      await startLocationTracking();
      setIsSharing(true);
      
      toast.success('Condivisione posizione attivata');
      return true;
    } catch (error) {
      console.error('Error enabling location sharing:', error);
      toast.error('Errore nell\'attivazione della condivisione posizione');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const disableLocationSharing = async () => {
    if (!user) return;

    setLoading(true);
    try {
      console.log('Disabling location sharing...');
      // Stop tracking
      await stopLocationTracking();
      
      // Update database
      const { error } = await supabase
        .from('user_locations')
        .update({ 
          is_sharing_enabled: false,
          last_updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (error) throw error;

      setIsSharing(false);
      setCurrentLocation(null);
      
      toast.success('Condivisione posizione disattivata');
    } catch (error) {
      console.error('Error disabling location sharing:', error);
      toast.error('Errore nella disattivazione della condivisione posizione');
    } finally {
      setLoading(false);
    }
  };

  const toggleLocationSharing = async () => {
    if (isSharing) {
      await disableLocationSharing();
    } else {
      await enableLocationSharing();
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopLocationTracking();
    };
  }, []);

  return {
    isSharing,
    currentLocation,
    loading,
    isNative,
    enableLocationSharing,
    disableLocationSharing,
    toggleLocationSharing,
    getCurrentPosition,
    requestLocationPermission
  };
};